import { useQueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { useCallback } from 'react';

import { Account } from '@air/api';
import { matchesAirror, WORKSPACE_INTEGRATION_SSO_NOT_ENABLED } from '@air/errors';
import { getCurrentSessionKey } from '@air/utils-auth';
import { reportErrorToBugsnag } from '@air/utils-error';

import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { getAccountKey } from '~/swr-hooks/account/useAccount';
import { getListApprovedWorkspacesKey } from '~/swr-hooks/workspaces/useListApprovedWorkspaces';
import { redirectToVerifyUser } from '~/utils/auth/authResumeRedirect';
import { getSkipOnboardingEnabledFromQuery } from '~/utils/getSkipOnboardingEnabledFromQuery';

const getQueryParamFromRedirect = (redirect: string | undefined, paramName: QueryParamNames): string | undefined => {
  if (!redirect) {
    return undefined;
  }

  try {
    const url = new URL(redirect, window.location.origin);

    return url.searchParams.get(paramName) ?? undefined;
  } catch {
    return undefined;
  }
};

export const useHandleNoWorkspaces = () => {
  const queryClient = useQueryClient();

  const handleNoWorkspaces = useCallback(
    async (redirect?: string) => {
      Router.ready(async () => {
        // Capture before awaits: HomeLayout's useRedirectUnverifiedUser can navigate
        // to `/verify-user` while these fetches run, which would make a late
        // Router.asPath read point at the verify gate instead of the skip-onboarding URL.
        const currentAsPath = Router.asPath;

        const [account, approvedWorkspaces] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: getAccountKey(),
            queryFn: () => Account.get(),
          }),
          queryClient.fetchQuery({
            queryKey: getListApprovedWorkspacesKey(),
            queryFn: () =>
              Account.listApprovedWorkspaces().catch((error) => {
                if (matchesAirror(error, WORKSPACE_INTEGRATION_SSO_NOT_ENABLED)) {
                  return { data: [] };
                } else {
                  throw error;
                }
              }),
          }),
        ]);

        void queryClient.invalidateQueries({ queryKey: getCurrentSessionKey() }).catch((error: unknown) => {
          reportErrorToBugsnag({
            error,
            context: 'Failed to invalidate session after handling no workspaces',
          });
        });

        if (!account.emailVerified) {
          // HomeLayout's useCheckIfUserHasWorkspaces calls this with no `redirect`.
          // Fall back to the live asPath so `/home?skipOnboarding=…` survives the
          // verify-user gate (and wins the race against useRedirectUnverifiedUser,
          // which also passes asPath). Bare `/home` is still elided by
          // redirectToVerifyUser.
          void redirectToVerifyUser({ router: Router, redirectTarget: redirect ?? currentAsPath });
          return;
        }

        const skipOnboardingFromRedirect = getQueryParamFromRedirect(redirect, QueryParamNames.skipOnboarding);
        const shouldSkipOnboarding =
          skipOnboardingFromRedirect === 'true' ||
          (skipOnboardingFromRedirect === undefined && getSkipOnboardingEnabledFromQuery(Router.query));

        if (shouldSkipOnboarding) {
          // From `/sign-up` (redirect provided): push so HomeLayout mounts and
          // useHandleSkipOnboarding can create the workspace; the redirect URL already
          // carries skipOnboarding=true plus any deep-link params (canvasName, etc.).
          // From inside HomeLayout (useCheckIfUserHasWorkspaces, no redirect): we're
          // already on the right surface — pushing to Routes.home would strip the live
          // query and race useHandleSkipOnboarding before it can read the deep-link.
          if (redirect) {
            Router.push(redirect);
          }

          return;
        }

        /**
         * Unverified users already went to `/verify-user`. We don't offer the
         * join-approved screen to unverified users because malicious users could
         * join workspaces with emails that aren't really theirs.
         */
        if (!approvedWorkspaces.data.length) {
          Router.push(Routes.onboarding.createWorkspace);
        } else {
          Router.push({
            pathname: Routes.joinApprovedWorkspace,
            query: {
              redirect,
            },
          });
        }
      });
    },
    [queryClient],
  );

  return {
    handleNoWorkspaces,
  };
};
